import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Download } from 'lucide-react';

/**
 * Full-screen modal to display a single image or video.
 *
 * Usage:
 *   <MediaLightbox
 *     open={Boolean(openMedia)}
 *     type={openMedia?.type}    // 'image' | 'video'
 *     src={openMedia?.src}
 *     onClose={() => setOpenMedia(null)}
 *   />
 *
 * - Renders in a portal so it escapes any overflow:hidden ancestor.
 * - Closes on backdrop click, ESC, or close button.
 * - Provides a download button (best-effort via blob).
 */
const MediaLightbox = ({ open, type, src, alt, onClose }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    // Prevent body scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Pause video when closing
      try { videoRef.current?.pause(); } catch (_) {}
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  const download = async (e) => {
    e.stopPropagation();
    try {
      const resp = await fetch(src);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = (type === 'video' ? 'mp4' : 'jpg');
      a.download = `destine-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {
      // Fallback: open in new tab so the user can long-press to save.
      window.open(src, '_blank');
    }
  };

  const node = (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="media-lightbox"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Close button (top-right) */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-3 right-3 p-2.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md transition-colors z-10"
        style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
        data-testid="lightbox-close-btn"
        aria-label="Fermer"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Download button (top-left) */}
      <button
        onClick={download}
        className="absolute top-3 left-3 p-2.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md transition-colors z-10"
        style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
        data-testid="lightbox-download-btn"
        aria-label="Télécharger"
      >
        <Download className="w-5 h-5 text-white" />
      </button>

      {/* Media (stops propagation so click on it doesn't close) */}
      <div
        className="max-w-full max-h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {type === 'video' ? (
          <video
            ref={videoRef}
            src={src}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-[90vh] rounded-lg"
            data-testid="lightbox-video"
          />
        ) : (
          <img
            src={src}
            alt={alt || ''}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            data-testid="lightbox-image"
            draggable={false}
          />
        )}
      </div>
    </div>
  );

  // Portal to document.body so we escape any overflow-hidden container.
  return ReactDOM.createPortal(node, document.body);
};

export default MediaLightbox;
